/**
 * Supported States Agent.
 *
 * - useSupportedStates(): public; returns enabled state codes for any
 *   authenticated user (used by Documents picker, AcceptedCelebration, etc).
 * - useAdminSupportedStates(): admin-only; full list + the 50-state catalog
 *   plus a mutator to enable/disable a state and edit notes.
 */

import { useState, useEffect, useCallback } from 'react';
import APIAgent from './apiAgent';

const PUBLIC_ENDPOINT = '/api/supported-states';
const ADMIN_ENDPOINT = '/api/admin/supported-states';

/** Public hook for any authenticated user. */
export function useSupportedStates() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(PUBLIC_ENDPOINT, 'GET');
      setCodes(Array.isArray(data?.states) ? data.states : []);
    } catch (err) {
      setError(err?.message || 'Failed to load supported states');
      setCodes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const isSupported = useCallback(
    (state) => Boolean(state) && codes.includes(String(state).toUpperCase()),
    [codes]
  );

  return { codes, loading, error, refresh, isSupported };
}

/** Admin hook: full catalog + per-row update. */
export function useAdminSupportedStates() {
  const [available, setAvailable] = useState([]); // 50 + DC, merged with row state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(ADMIN_ENDPOINT, 'GET');
      setAvailable(Array.isArray(data?.available) ? data.available : []);
      setIsAdmin(true);
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('403') || msg.toLowerCase().includes('admin')) {
        setIsAdmin(false);
      } else {
        setError(msg || 'Failed to load supported states');
        setIsAdmin(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const updateState = useCallback(
    async (code, { enabled, notes }) => {
      await APIAgent.request(
        `${ADMIN_ENDPOINT}/${encodeURIComponent(code)}`,
        'PUT',
        { enabled, notes: notes ?? null }
      );
      await refresh();
    },
    [refresh]
  );

  return { available, loading, error, isAdmin, updateState, refresh };
}
